import React, { useState } from 'react';
import styled from 'styled-components';
import alldayMenu from '../assets/menu/allday.png';
import lunchMenu from '../assets/menu/lunch.png';
import drinkMenu from '../assets/menu/drink.png';
import specialsMenu from '../assets/menu/specials.png';
import { theme } from '../theme';

const SectionContainer = styled.section`
  width: 100%;
  padding: 110px 20px;
  background-color: ${theme.colors.light};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${theme.colors.burgundy}, transparent);
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    padding: 90px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 70px 15px;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${theme.fonts.heading};
  font-size: 3.25rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 15px;
  text-align: center;
  position: relative;
  letter-spacing: 1px;
  
  &::after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.caramel};
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.3rem;
  }
`;

const SectionSubtitle = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 1.2rem;
  color: ${theme.colors.taupe};
  text-align: center;
  margin: 35px 0 50px;
  font-style: italic;
  max-width: 700px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 35px 0 40px;
    max-width: 90%;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin: 30px 0 35px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1300px;
  display: flex;
  flex-direction: column;
  gap: 90px;
`;

const HoursCard = styled.div`
  background-color: white;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  padding: 50px;
  border-left: 3px solid ${theme.colors.burgundy};
  position: relative;
  overflow: hidden;
  transform: translateZ(0);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.large};
  }
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(to right, transparent, ${theme.colors.taupe}, transparent);
    opacity: 0.2;
  }
  
  @media (max-width: 768px) {
    padding: 40px 30px;
    border-left: 2px solid ${theme.colors.burgundy};
  }
  
  @media (max-width: 480px) {
    padding: 30px 20px;
  }
`;

const HoursTitle = styled.h3`
  font-family: ${theme.fonts.heading};
  font-size: 2.2rem;
  font-weight: 600;
  color: ${theme.colors.secondary};
  margin-bottom: 40px;
  text-align: center;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, rgba(114, 47, 55, 0) 0%, ${theme.colors.burgundy} 50%, rgba(114, 47, 55, 0) 100%);
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 25px;
  }
`;

const HoursList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 30px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-top: 20px;
  }
`;

const HoursItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 25px;
  background-color: rgba(245, 241, 237, 0.5);
  border-radius: ${theme.borderRadius.small};
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(139, 69, 19, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.small};
    background-color: rgba(255, 255, 255, 0.95);
    border-left: 2px solid ${theme.colors.caramel};
  }
`;

const Day = styled.span`
  font-family: ${theme.fonts.accent};
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.dark};
  letter-spacing: 0.5px;
`;

const Time = styled.span`
  font-family: ${theme.fonts.body};
  font-size: 1.05rem;
  font-weight: 500;
  color: ${theme.colors.primary};
  letter-spacing: 0.5px;
  font-style: italic;
`;

const MenuContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    margin-bottom: 30px;
  }
`;

const Tab = styled.button<{ isActive: boolean }>`
  font-family: ${theme.fonts.accent};
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${props => (props.isActive ? theme.colors.light : theme.colors.primary)};
  background-color: ${props => (props.isActive ? theme.colors.burgundy : 'transparent')};
  border: 1px solid ${props => (props.isActive ? theme.colors.burgundy : theme.colors.primary)};
  border-radius: ${theme.borderRadius.small};
  padding: 14px 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => (props.isActive ? theme.shadows.small : 'none')};
  transform: ${props => (props.isActive ? 'translateY(-2px)' : 'none')};
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: ${props => (props.isActive ? theme.colors.secondary : 'rgba(139, 69, 19, 0.05)')};
    transform: translateY(-2px);
    border-color: ${props => (props.isActive ? theme.colors.secondary : theme.colors.caramel)};
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 0.9rem;
    letter-spacing: 1.5px;
  }
  
  @media (max-width: 480px) {
    width: 80%;
    padding: 12px 20px;
  }
`;

const MenuImage = styled.img`
  width: 100%;
  max-width: 800px;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  transition: all 0.3s ease;
  border: 1px solid rgba(139, 69, 19, 0.1);
  
  &:hover {
    transform: scale(1.01);
    box-shadow: ${theme.shadows.large};
  }
  
  @media (max-width: 480px) {
    border-radius: ${theme.borderRadius.small};
  }
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HoursAndMenu: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'allday' | 'lunch' | 'drinks' | 'specials'>('allday');

  return (
    <SectionContainer id="menu">
      <SectionTitle>Hours & Menu</SectionTitle>
      <SectionSubtitle>Discover our culinary offerings and plan your visit</SectionSubtitle>
      <ContentWrapper>
        <HoursCard>
          <HoursTitle>Hours of Operation</HoursTitle>
          <HoursList>
            <HoursItem>
              <Day>Monday</Day>
              <Time>11:00 AM - 9:00 PM</Time>
            </HoursItem>
            <HoursItem>
              <Day>Tuesday</Day>
              <Time>11:00 AM - 9:00 PM</Time>
            </HoursItem>
            <HoursItem>
              <Day>Wednesday</Day>
              <Time>11:00 AM - 9:00 PM</Time>
            </HoursItem>
            <HoursItem>
              <Day>Thursday</Day>
              <Time>11:00 AM - 9:00 PM</Time>
            </HoursItem>
            <HoursItem>
              <Day>Friday</Day>
              <Time>11:00 AM - 10:00 PM</Time>
            </HoursItem>
            <HoursItem>
              <Day>Saturday</Day>
              <Time>11:00 AM - 10:00 PM</Time>
            </HoursItem>
            <HoursItem>
              <Day>Sunday</Day>
              <Time>11:00 AM - 9:00 PM</Time>
            </HoursItem>
          </HoursList>
        </HoursCard>

        <MenuContainer>
          <TabsContainer>
            <Tab
              isActive={activeTab === 'allday'}
              onClick={() => setActiveTab('allday')}
            >
              All Day
            </Tab>
            <Tab
              isActive={activeTab === 'lunch'}
              onClick={() => setActiveTab('lunch')}
            >
              Lunch
            </Tab>
            <Tab
              isActive={activeTab === 'drinks'}
              onClick={() => setActiveTab('drinks')}
            >
              Drinks
            </Tab>
            <Tab
              isActive={activeTab === 'specials'}
              onClick={() => setActiveTab('specials')}
            >
              Specials
            </Tab>
          </TabsContainer>

          <MenuSection>
            {activeTab === 'allday' && (
              <MenuImage src={alldayMenu} alt="All Day Menu" />
            )}
            {activeTab === 'lunch' && (
              <MenuImage src={lunchMenu} alt="Lunch Menu" />
            )}
            {activeTab === 'drinks' && (
              <MenuImage src={drinkMenu} alt="Drinks Menu" />
            )}
            {activeTab === 'specials' && (
              <MenuImage src={specialsMenu} alt="Specials Menu" />
            )}
          </MenuSection>
        </MenuContainer>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default HoursAndMenu; 